import React, { useState, useEffect, useCallback } from 'react';
import {
  getCrmPromotions, createCrmPromotion, getCrmPromotion, previewCrmPromotion,
  getCrmPromotionWalletOptions, getCrmPromotionStatus, runCrmPromotion, undoCrmPromotion,
} from '../../services/api';
import { RfmSelect } from './rfmSegments';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatMoney, formatDate } from '../../utils/formatters';

/* Иконки рисуем штрихом вместо эмодзи (см. Navbar.jsx). */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IconSearch = () => <Icon><circle cx="11.5" cy="11.5" r="9.5" /><path d="M18.5 18.5L22 22" /></Icon>;
const IconWarning = () => <Icon><path d="M5.31171 10.7615C8.23007 5.58716 9.68925 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.31034 17.856 2.52291 15.7061 4.94805 11.4063L5.31171 10.7615Z" /><path d="M12 8V13" /><path d="M12 16H12.0001" /></Icon>;
const IconCheckCircle = () => <Icon><circle cx="12" cy="12" r="10" /><path d="M8.5 12.5L10.5 14.5L15.5 9.5" /></Icon>;
const IconSkip = () => <Icon><path d="M16.6598 9.35258C18.4467 10.5065 18.4467 13.4935 16.6598 14.6474L5.87083 21.6145C4.13419 22.736 2 21.2763 2 18.9671L2 5.0329C2 2.72368 4.13419 1.26402 5.87083 2.38548L16.6598 9.35258Z" /><path d="M22 5V19" /></Icon>;

const STATUS_TITLES = { active: 'Активные', risk: 'Под риском', lost: 'Потерянные' };
const PROMO_STATUS_TITLES = { draft: 'Черновик', running: 'Выполняется', done: 'Завершена', reversed: 'Отменена', interrupted: 'Прервана', reversing: 'Отменяется', reverse_partial: 'Отменена не полностью' };
// Откуда можно отменить начисление — совпадает с UNDOABLE_CAMPAIGN_STATUSES в server.py.
const UNDOABLE = new Set(['done', 'interrupted', 'reverse_partial']);
const WARN_STATUSES = new Set(['running', 'reversing', 'interrupted', 'reverse_partial']);
const RESULT_STATUS_TITLES = {
  pending: 'Ожидает', credited: 'Начислено', not_found: 'Не найден в Cloud',
  skipped_no_wallet: 'Нет такого кошелька', failed: 'Ошибка', reversed: 'Списано обратно',
};

const NewPromotionForm = ({ onCreated }) => {
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState('');
  const [tag, setTag] = useState('');
  const [search, setSearch] = useState('');
  const [rfm, setRfm] = useState('');
  const [walletName, setWalletName] = useState('');
  const [walletOptions, setWalletOptions] = useState([]);
  const [walletError, setWalletError] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [walletBusy, setWalletBusy] = useState(false);

  const filters = { status, tag, search, rfm };

  useEffect(() => {
    let cancelled = false;
    previewCrmPromotion(filters).then((res) => { if (!cancelled) setPreview(res); }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, tag, search, rfm]);

  const loadWallets = async () => {
    setWalletBusy(true);
    setWalletError('');
    try {
      const res = await getCrmPromotionWalletOptions(filters);
      setWalletOptions(res.wallets || []);
      if (res.error) setWalletError(res.error);
      else if (res.wallets?.length) setWalletName(res.wallets[0]);
    } catch (err) {
      setWalletError(err.response?.data?.error || 'Не удалось получить список кошельков');
    } finally {
      setWalletBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !walletName || !amount) return;
    setBusy(true);
    try {
      await createCrmPromotion({
        name: name.trim(), comment: comment.trim() || null,
        amount: Number(amount), wallet_name: walletName, filters,
      });
      setName(''); setComment(''); setAmount('');
      setStatus(''); setTag(''); setSearch(''); setRfm('');
      setWalletName(''); setWalletOptions([]);
      onCreated();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="panel crm-promo-form" onSubmit={submit}>
      <h4>Новая акция</h4>
      <div className="crm-promo-form-row">
        <input type="text" placeholder="Название акции" value={name} onChange={(e) => setName(e.target.value)} />
        <input type="text" placeholder="Комментарий (необязательно)" value={comment} onChange={(e) => setComment(e.target.value)} />
      </div>
      <div className="crm-promo-form-row">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Любой статус</option>
          {Object.entries(STATUS_TITLES).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
        <input type="text" placeholder="Тег" value={tag} onChange={(e) => setTag(e.target.value)} />
        <RfmSelect value={rfm} onChange={setRfm} />
        <input type="text" placeholder="Поиск по имени/телефону/карте" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {preview && (
        <div className="crm-promo-preview">
          Подходит под сегмент: <strong>{preview.matched}</strong> гостей.
          Начислить бонус в iiko можно только тем, у кого известен телефон: <strong>{preview.with_phone}</strong>.
        </div>
      )}
      <div className="crm-promo-form-row">
        <input type="number" min="1" placeholder="Сумма бонуса, ₽" value={amount}
               onChange={(e) => setAmount(e.target.value)} />
        <select value={walletName} onChange={(e) => setWalletName(e.target.value)} disabled={!walletOptions.length}>
          <option value="">{walletOptions.length ? 'Кошелёк' : 'Сначала проверьте кошельки'}</option>
          {walletOptions.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
        <button type="button" className="ui-btn-ghost" onClick={loadWallets} disabled={walletBusy}>
          {walletBusy ? 'Проверяю…' : <><IconSearch /> Проверить кошельки</>}
        </button>
      </div>
      {walletError && <div className="crm-promo-error"><IconWarning /> {walletError}</div>}
      <button className="btn-primary" type="submit" disabled={busy || !name.trim() || !walletName || !amount}>
        Создать акцию (черновик — деньги пока не списываются)
      </button>
    </form>
  );
};

const PromotionResults = ({ promotionId }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getCrmPromotion(promotionId).then((res) => { if (!cancelled) setData(res); });
    return () => { cancelled = true; };
  }, [promotionId]);

  if (!data) return <LoadingSpinner />;
  return (
    <div className="table-wrapper crm-promo-results">
      <table>
        <thead>
          <tr><th>Гость</th><th>Телефон</th><th>Статус</th><th>Сумма</th><th>Когда</th></tr>
        </thead>
        <tbody>
          {data.results.map((r) => (
            <tr key={r.id}>
              <td>{r.name || r.card_number || '—'}</td>
              <td>{r.phone || '—'}</td>
              <td>{RESULT_STATUS_TITLES[r.status] || r.status}{r.detail ? ` — ${r.detail}` : ''}</td>
              <td>{r.amount != null ? formatMoney(r.amount) : '—'}</td>
              <td>{r.credited_at ? formatDate(r.credited_at) : '—'}</td>
            </tr>
          ))}
          {data.results.length === 0 && (
            <tr><td colSpan={5} className="crm-empty">Пока пусто — акция ещё не запускалась</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const PromotionRow = ({ promo, onStarted, progress }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    const ok = window.confirm(
      `Будет начислено ${promo.amount} ₽ бонусами каждому подходящему гостю ` +
      `(кошелёк «${promo.wallet_name}») реальными деньгами в iiko. ` +
      `Действие сложно отменить одним кликом. Продолжить?`
    );
    if (!ok) return;
    setBusy(true);
    try {
      await runCrmPromotion(promo.id);
      onStarted();
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    const ok = window.confirm(`Списать обратно все бонусы, начисленные по акции «${promo.name}»? ` +
      `Если гость уже успел их потратить, списание уйдёт в минус его баланса в iiko.`);
    if (!ok) return;
    setBusy(true);
    try {
      await undoCrmPromotion(promo.id);
      onStarted();
    } finally {
      setBusy(false);
    }
  };

  const isRunningThis = progress?.running && progress.promotion_id === promo.id;
  const badgeClass = promo.status === 'done' ? 'st-active'
    : promo.status === 'reversed' ? 'st-lost'
    : WARN_STATUSES.has(promo.status) ? 'st-risk' : 'st-unknown';

  return (
    <>
      <tr className="crm-row" onClick={() => setExpanded((v) => !v)}>
        <td>
          <div className="gb-name">{promo.name}</div>
          {promo.comment && <div className="gb-owner">{promo.comment}</div>}
        </td>
        <td className="gb-money">{formatMoney(promo.amount)}</td>
        <td>{promo.wallet_name}</td>
        <td><span className={`gb-badge ${badgeClass}`}>{PROMO_STATUS_TITLES[promo.status] || promo.status}</span></td>
        <td>
          {isRunningThis
            ? `${progress.checked} / ${progress.total}`
            : <><IconCheckCircle /> {promo.credited_count} · <IconSkip /> {promo.skipped_count} · <IconWarning /> {promo.failed_count}</>}
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          {promo.status === 'draft' && (
            <button className="ui-btn-ghost" onClick={run} disabled={busy}>Запустить</button>
          )}
          {UNDOABLE.has(promo.status) && (
            <button className="ui-btn-ghost" onClick={undo} disabled={busy}>Отменить начисление</button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr><td colSpan={6}><PromotionResults promotionId={promo.id} /></td></tr>
      )}
    </>
  );
};

const PromotionsTab = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [pollTrigger, setPollTrigger] = useState(0);

  const load = useCallback(async () => {
    const res = await getCrmPromotions();
    setPromotions(res.promotions || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Опрашиваем прогресс, только пока реально что-то выполняется — вместо
  // постоянного интервала, который бы дёргал бэкенд и когда акций никто не
  // запускал. pollTrigger форсирует повторный запуск сразу после run/undo.
  useEffect(() => {
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const res = await getCrmPromotionStatus();
        if (cancelled) return;
        setProgress(res);
        if (res.running) {
          timer = setTimeout(poll, 2000);
        } else {
          load();
        }
      } catch {
        // тихо — просто перестаём опрашивать
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [load, pollTrigger]);

  return (
    <>
      <NewPromotionForm onCreated={load} />

      {loading && <LoadingSpinner />}

      {!loading && (
        <div className="panel gb-table-panel">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Акция</th><th>Сумма</th><th>Кошелёк</th><th>Статус</th><th>Итог</th><th></th></tr>
              </thead>
              <tbody>
                {promotions.map((p) => (
                  <PromotionRow
                    key={p.id} promo={p}
                    onStarted={() => setPollTrigger((n) => n + 1)}
                    progress={progress}
                  />
                ))}
                {promotions.length === 0 && (
                  <tr><td colSpan={6} className="crm-empty">Пока нет акций</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default PromotionsTab;
